import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Avatar } from './Avatar';
import { FalloDePanel, Panel, PieDePanel, VacioDePanel } from './Panel';
import { Skeleton } from '@/ui/Skeleton';

/**
 * PanelesDeEquipo — los tres bloques laterales que hablan del equipo.
 *
 * Top reconocimientos, Celebraciones de hoy y Próximos eventos. Los monta
 * Inicio y los monta Novedades, y hasta el 2026-09-08 había **dos versiones**:
 * la de `modules/widgets.js` (vanilla, la del muro) y la de `Inicio.tsx` (React,
 * la del inicio). Ya habían divergido: la vanilla pintaba la insignia de
 * celebración en rosa y naranja con hexadecimales a mano, y la React lo corrigió
 * el 2026-09-07 sin que la otra se enterara.
 *
 * ## Cada panel pregunta por su módulo ANTES de pedir
 *
 * `/kudos/leaderboard` exige `require_module("kudos")` (`content.py:999`). La
 * versión vanilla lo pedía siempre, así que a un rol con Novedades y sin
 * Reconocimientos el servidor le contestaba 403 y el panel decía **«No se pudo
 * cargar»** — indistinguible de una caída de red, cuando lo que pasa es que esa
 * sección no está en su rol y reintentar no lo va a arreglar nunca.
 *
 * Por eso `habilitado` no es opcional en el ranking: quien monta el panel pasa
 * `Auth.hasModule('kudos')`, que es lo mismo que consulta el menú lateral en su
 * `data-module`. No es control de acceso —quien autoriza sigue siendo el
 * servidor—: es no ofrecer una puerta que se sabe cerrada.
 *
 * Los otros dos no llevan guard de módulo en el servidor **a propósito**
 * (`events.py:79`, `celebrations.py:135`), así que aquí tampoco.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

export interface FilaDelRanking {
  user_id?: number;
  full_name: string;
  total_kudos: number;
}

export interface PersonaQueCelebra {
  full_name: string;
  years?: number | null;
}

export interface CelebracionesDeHoy {
  birthdays?: PersonaQueCelebra[];
  anniversaries?: PersonaQueCelebra[];
}

export interface EventoProximo {
  id: number;
  title: string;
  event_date: string;
  is_registered?: boolean;
}

/* ── Consultas ─────────────────────────────────────────────────────────── */

export const clavesDeEquipo = {
  ranking: ['equipo', 'ranking'] as const,
  celebraciones: ['equipo', 'celebraciones'] as const,
  proximos: ['equipo', 'proximos'] as const,
};

/* ── Top reconocimientos ───────────────────────────────────────────────── */

export function TopReconocimientos({ habilitado }: { habilitado: boolean }) {
  const consulta = useQuery({
    queryKey: clavesDeEquipo.ranking,
    queryFn: () => api.get<FilaDelRanking[]>('/kudos/leaderboard'),
    enabled: habilitado,
  });

  // Sin el módulo no hay panel. Con `enabled: false` la consulta se queda en
  // `isPending` para siempre, así que el esqueleto latiría eternamente.
  if (!habilitado) return null;

  // Oro, plata y bronce. Se quedan como literales porque son las medallas de un
  // podio y no colores de marca: no cambian con la instalación ni con el tema.
  // La tinta es #3a2a00 sobre los tres (9,91 / 7,64 / 4,42 de contraste).
  const medallas = ['#FFD700', '#C0C0C0', '#CD7F32'];
  return (
    <Panel
      icono="bi-trophy-fill"
      titulo="Top reconocimientos"
      pie={<PieDePanel href="#kudos">Ver todos</PieDePanel>}
    >
      {consulta.isPending ? (
        <Skeleton className="h-20" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="los reconocimientos"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !consulta.data?.length ? (
        <VacioDePanel>Aún no hay reconocimientos.</VacioDePanel>
      ) : (
        <ol className="flex flex-col">
          {consulta.data.slice(0, 5).map((u, i) => (
            <li key={`${u.full_name}-${i}`}>
              <a
                href="#kudos"
                className="flex items-center gap-2.5 rounded-sm px-1 py-1.5 transition-colors
                           hover:bg-sunken focus-visible:focus-anillo
                           pointer-coarse:min-h-11"
              >
                <span
                  className="grid size-5.5 shrink-0 place-items-center rounded-full bg-line
                             text-2 font-extrabold text-muted"
                  style={i < 3 ? { background: medallas[i], color: '#3a2a00' } : undefined}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <Avatar nombre={u.full_name} tamano={32} />
                <span className="min-w-0 flex-1 truncate text-3 font-semibold text-ink">
                  {u.full_name}
                </span>
                {/* El recuento es un DATO, no una acción: va en navy como el
                    resto de las cifras. El acento se reserva para dónde actuar. */}
                <span className="shrink-0 text-2 font-extrabold tabular-nums text-brand-ink">
                  <i className="bi bi-star-fill" aria-hidden="true" /> {u.total_kudos}
                </span>
              </a>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

/* ── Celebraciones de hoy ──────────────────────────────────────────────── */

/** El panel lateral de quién celebra HOY. Se llamaba `Celebraciones` a secas
 *  hasta el 2026-09-11, y ese día pasó a haber una PANTALLA con ese nombre
 *  (`features/celebraciones/`). Dos cosas distintas con el mismo nombre son
 *  dos cosas que alguien va a confundir; sus dos hermanos de este archivo ya
 *  eran específicos (`TopReconocimientos`, `ProximosEventos`). */
export function PanelDeCelebraciones() {
  const consulta = useQuery({
    queryKey: clavesDeEquipo.celebraciones,
    queryFn: () => api.get<CelebracionesDeHoy>('/celebrations/today'),
  });

  const gente = [
    ...(consulta.data?.birthdays ?? []).map((u) => ({ ...u, cumple: true })),
    ...(consulta.data?.anniversaries ?? []).map((u) => ({ ...u, cumple: false })),
  ];

  // Sin celebraciones no hay tarjeta. Un panel que dice «hoy no cumple nadie»
  // ocupa sitio todos los días para informar de nada.
  if (!consulta.isError && !consulta.isPending && !gente.length) return null;

  return (
    <Panel icono="bi-cake2-fill" titulo="Celebraciones de hoy">
      {consulta.isPending ? (
        <Skeleton className="h-14" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="las celebraciones"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <ul className="flex flex-col">
          {gente.map((u, i) => (
            <li
              key={`${u.full_name}-${i}`}
              className="flex items-center gap-2.5 border-b border-line py-2 last:border-b-0"
            >
              <span className="relative shrink-0">
                <Avatar nombre={u.full_name} tamano={38} />
                {/* La insignia iba rosa o naranja según el tipo. El ICONO ya
                    distingue pastel de galardón, y la palabra de abajo lo dice
                    con letras: dos señales sobraban y una era un hexadecimal
                    fuera del sistema de tokens. */}
                <span
                  className="absolute -bottom-0.5 -right-0.5 grid size-[18px] place-items-center
                             rounded-full border-2 border-surface bg-brand text-1 text-on-brand"
                  aria-hidden="true"
                >
                  <i className={cn('bi', u.cumple ? 'bi-cake2-fill' : 'bi-award-fill')} />
                </span>
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-3 font-bold text-ink">{u.full_name}</span>
                <span className="block text-2 text-muted">
                  {u.cumple
                    ? 'Cumple años hoy'
                    : `${u.years ?? 0} ${u.years === 1 ? 'año' : 'años'} en la organización`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ── Próximos eventos ──────────────────────────────────────────────────── */

export function ProximosEventos() {
  const consulta = useQuery({
    queryKey: clavesDeEquipo.proximos,
    queryFn: () => api.get<EventoProximo[]>('/events/upcoming', { limit: 4 }),
  });

  return (
    <Panel
      icono="bi-calendar-event-fill"
      titulo="Próximos eventos"
      pie={<PieDePanel href="#eventos">Ver todos</PieDePanel>}
    >
      {consulta.isPending ? (
        <Skeleton className="h-20" />
      ) : consulta.isError ? (
        <FalloDePanel
          que="los eventos"
          err={consulta.error}
          onReintentar={() => void consulta.refetch()}
        />
      ) : !consulta.data?.length ? (
        <VacioDePanel>Sin eventos próximos.</VacioDePanel>
      ) : (
        <ul className="flex flex-col">
          {consulta.data.map((e) => {
            const d = new Date(e.event_date);
            return (
              <li key={e.id} className="border-b border-line last:border-b-0">
                <a
                  // Al evento, no a la lista: `#eventos/<id>` abre su detalle
                  // desde el 2026-09-17.
                  href={`#eventos/${e.id}`}
                  className="flex items-center gap-3 rounded-sm px-1 py-2 transition-colors
                             hover:bg-sunken focus-visible:focus-anillo
                             pointer-coarse:min-h-11"
                >
                  <span
                    className="w-11 shrink-0 rounded-sm bg-info/10 py-1 text-center"
                    aria-hidden="true"
                  >
                    <span className="block text-5 font-extrabold leading-none text-info-ink">
                      {d.getDate()}
                    </span>
                    <span className="block text-1 font-bold uppercase text-info-ink">
                      {d.toLocaleDateString('es-CO', { month: 'short' }).replace('.', '')}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-3 font-bold text-ink">{e.title}</span>
                    <span className="block text-2 text-muted">
                      <i className="bi bi-clock" aria-hidden="true" />{' '}
                      {d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                      {e.is_registered && (
                        <>
                          {' · '}
                          <span className="text-ok-ink">
                            <i className="bi bi-check-circle-fill" aria-hidden="true" /> Inscrito
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
